import { AsCache, WithOwner } from "../IDBSchema";
import { MaterialBase } from "../../../schema/generated/Material";
import { EntityDataApi, IdbRowPayload } from "../IndexedDBApi";
import Time from "../../../util/Time";
import { getFromIdPayload, getFromMaterialIDPayload } from "../../../schema/general";
import { CarryInForMaterialSearch, ConstructionForMaterialSearch, getMaterialFromIdSearchPayload, IssuanceForMaterialSearch, LotForMaterialSearch, MaterialConsumption, searchedMaterial, searchedMaterialRows } from "../../../schema/console/material";
import { idbEntityDefs } from "../generated/entityDefs";
import { objectKeys, required } from "../../../util/typed";
import { isIdInRanges } from "../../../util/Materialperser";

// Lot/CarryIn/Constructionのprocess(QRスキャン)から材料の紐付けを更新する際のペイロード
type materialLink = {
  id: number;
  issuance_id?: string | null;
  lot_id?: string | null;
  carry_in_id?: string | null;
};

// patch の定義済み列だけを既存行へ上書きする(未指定列は既存値を保持する列独立マージ)。
// Object.entries だとキーと値の型相関が失われるため、キー単位のヘルパで型を保ったまま代入する。
// eslint-disable-next-line @typescript-eslint/ban-types -- ジェネリクスの「非プリミティブ」制約としての object は正当
function copyColumnIfDefined<T extends object, K extends keyof T>(target: T, source: Partial<T>, key: K): void {
  const value: T[K] | undefined = source[key];
  if (value !== undefined) {
    target[key] = value;
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types -- 同上(非プリミティブ制約)
function mergeDefinedColumns<T extends object>(base: T, patch: Partial<T>): T {
  const merged = { ...base };
  for (const key of objectKeys(patch)) {
    copyColumnIfDefined(merged, patch, key);
  }
  return merged;
}

export class MaterialDataApi extends EntityDataApi<MaterialBase> {
  public readonly def = idbEntityDefs.Material;

  public async getAll(ownerId: string): Promise<MaterialBase[]> {
    return await this.listByOwner(ownerId);
  }

  public async getByIssuanceId(issuanceId: string, ownerId: string): Promise<MaterialBase[]> {
    return await this.readByIndex("issuance_id", ownerId, issuanceId);
  }

  public async getByLotId(lotId: string, ownerId: string): Promise<MaterialBase[]> {
    return await this.readByIndex("lot_id", ownerId, lotId);
  }

  public async getByCarryInId(carryInId: string, ownerId: string): Promise<MaterialBase[]> {
    return await this.readByIndex("carry_in_id", ownerId, carryInId);
  }

  public async getByConstructionId(payload: WithOwner<getFromIdPayload>): Promise<MaterialBase[]> {
    // この施工が消費した材料QR個票を消費記録(ConstructionMaterialConsumption)から解決する。
    // construction_id 直リンクではなく真ソースを使うため、部分消費・分割消費の材料も含む(APIと同方針)。
    const consumptions = await this.IDB().generated.constructionMaterialConsumption.getAllByOwner(payload.ownerId);
    const qrIds = new Set(
      consumptions.filter((c) => c.construction_id === payload.id && c.deleted_at == null).map((c) => c.material_qr_id),
    );
    if (qrIds.size === 0) {
      return [];
    }
    const all = await this.getAll(payload.ownerId);
    return all.filter((m) => qrIds.has(m.id));
  }

  // 材料の代表施工を消費記録から解決する。1材料が複数施工へ分割消費されうるため、単一の代表を出す場合は
  // 「生きている施工を指す alive 消費のうち createdAt 最新」を代表とする(APIの attachConsumedConstructions と同方針)。
  // used_ratio=0 の参照専用行(他施工の使用済QRを紐付けただけの行)は実消費ではないため代表候補にしない。
  private async resolveRepresentativeConstruction(consumptions: MaterialConsumption[], ownerId: string): Promise<ConstructionForMaterialSearch | null> {
    const aliveConsumptions = consumptions.filter((c) => c.deleted_at == null && (c.used_ratio ?? 1) > 0);
    if (aliveConsumptions.length === 0) {
      return null;
    }
    const distinctIds = [...new Set(aliveConsumptions.map((c) => c.construction_id))];
    const aliveConstructionById = new Map<string, ConstructionForMaterialSearch>();
    await Promise.all(
      distinctIds.map(async (id) => {
        const construction = await this.IDB().constructionDataApi.getConstructionForMaterialSearch({ id, ownerId });
        if (construction != null && construction.deleted_at == null) {
          aliveConstructionById.set(id, construction);
        }
      }),
    );
    let chosen: MaterialConsumption | null = null;
    for (const consumption of aliveConsumptions) {
      if (aliveConstructionById.has(consumption.construction_id)) {
        if (chosen === null || consumption.createdAt > chosen.createdAt) {
          chosen = consumption;
        }
      }
    }
    return chosen != null ? aliveConstructionById.get(chosen.construction_id) ?? null : null;
  }

  // QRごとの消費明細(部分消費対応)。alive のみ・createdAt 昇順(サーバの Consumptions と同形)。
  // Construction の nest はサーバの include と同じ意味論で、IndexedDB の施工行(削除済み含む)から組み立てる。
  private async consumptionsByQrId(ownerId: string): Promise<Map<number, MaterialConsumption[]>> {
    const rows = await this.IDB().generated.constructionMaterialConsumption.getAllByOwner(ownerId);
    const constructions = await this.IDB().constructionDataApi.getAllByOwner(ownerId, false);
    const constructionById = new Map(constructions.map((construction) => [construction.id, construction]));
    const map = new Map<number, MaterialConsumption[]>();
    for (const row of rows) {
      const list = map.get(row.material_qr_id) ?? [];
      const construction = constructionById.get(row.construction_id);
      list.push({
        construction_id: row.construction_id,
        used_ratio: row.used_ratio ?? 1,
        createdAt: row.createdAt,
        deleted_at: row.deleted_at,
        Construction: construction != null ? { id: construction.id, deleted_at: construction.deleted_at } : null,
      });
      map.set(row.material_qr_id, list);
    }
    for (const list of Array.from(map.values())) {
      list.sort((a, b) => a.createdAt - b.createdAt);
    }
    return map;
  }

  public async getLifeSpanFromId(payload: WithOwner<getFromMaterialIDPayload>): Promise<searchedMaterial | null> {
    const material = await this.findById({ id: payload.id, ownerId: payload.ownerId });
    if (!material) {
      return null;
    }

    const [issuance, lot, carryIn] = await Promise.all([
      material.issuance_id
        ? this.IDB().issuanceDataApi.getForMaterialSearch({ id: material.issuance_id, ownerId: payload.ownerId })
        : null,
      material.lot_id
        ? this.IDB().lotDataApi.getLotForMaterialSearch({ id: material.lot_id, ownerId: payload.ownerId })
        : null,
      material.carry_in_id
        ? this.IDB().carryInDataApi.getCarryInForMaterialSearch({ id: material.carry_in_id, ownerId: payload.ownerId })
        : null,
    ]);

    const consumptionsMap = await this.consumptionsByQrId(payload.ownerId);
    const aliveConsumptions = (consumptionsMap.get(material.id) ?? []).filter((c) => c.deleted_at == null);
    // 代表施工は消費記録から解決する(construction_id 直リンクに依存しない)。
    const construction = await this.resolveRepresentativeConstruction(aliveConsumptions, payload.ownerId);
    return { ...material, Issuance: issuance, Lot: lot, CarryIn: carryIn, Construction: construction, Consumptions: aliveConsumptions };
  }

  public async getLifeSpanFromIds(payload: WithOwner<getMaterialFromIdSearchPayload>): Promise<searchedMaterialRows> {
    const { ownerId, targetRanges } = payload;

    // 1) マテリアル一覧は一度だけ取得
    const allMaterials = await this.getAll(ownerId);

    // 2) 対象範囲のマテリアルを抽出(範囲は展開せず含有判定。存在しないIDはここで落ちる)
    const materials = allMaterials.filter((m) => isIdInRanges(m.id, targetRanges));

    if (materials.length === 0) {
      return [];
    }

    // 3) 関連IDをユニーク化
    const issuanceIds = new Set<string>(materials.map((m) => m.issuance_id).filter((id): id is string => id != null));
    const lotIds = new Set<string>(materials.map((m) => m.lot_id).filter((id): id is string => id != null));
    const carryInIds = new Set<string>(materials.map((m) => m.carry_in_id).filter((id): id is string => id != null));

    // 4) 各マスタを並列で一括取得(ユニークIDごとに1回だけ叩く)
    const [issuanceMap, lotMap, carryInMap] = await Promise.all([
      (async () => {
        const map = new Map<string, IssuanceForMaterialSearch>();
        await Promise.all(
          Array.from(issuanceIds).map(async (id) => {
            const data = await this.IDB().issuanceDataApi.getForMaterialSearch({ id, ownerId });
            map.set(id, data);
          })
        );
        return map;
      })(),
      (async () => {
        const map = new Map<string, LotForMaterialSearch>();
        await Promise.all(
          Array.from(lotIds).map(async (id) => {
            const data = await this.IDB().lotDataApi.getLotForMaterialSearch({ id, ownerId });
            map.set(id, data);
          })
        );
        return map;
      })(),
      (async () => {
        const map = new Map<string, CarryInForMaterialSearch>();
        await Promise.all(
          Array.from(carryInIds).map(async (id) => {
            const data = await this.IDB().carryInDataApi.getCarryInForMaterialSearch({ id, ownerId });
            map.set(id, data);
          })
        );
        return map;
      })(),
    ]);

    const consumptionsMap = await this.consumptionsByQrId(ownerId);

    // 代表施工は消費記録から解決する(construction_id 直リンクに依存しない)。全材料の alive 消費が指す
    // 施工のうち生存しているものを一括解決し、材料ごとに createdAt 最新の生存施工を代表とする。
    const allAliveConstructionIds = new Set<string>();
    for (const m of materials) {
      for (const consumption of consumptionsMap.get(m.id) ?? []) {
        if (consumption.deleted_at == null) {
          allAliveConstructionIds.add(consumption.construction_id);
        }
      }
    }
    const aliveConstructionById = new Map<string, ConstructionForMaterialSearch>();
    await Promise.all(
      Array.from(allAliveConstructionIds).map(async (id) => {
        const construction = await this.IDB().constructionDataApi.getConstructionForMaterialSearch({ id, ownerId });
        if (construction != null && construction.deleted_at == null) {
          aliveConstructionById.set(id, construction);
        }
      }),
    );
    const representativeConstruction = (consumptions: MaterialConsumption[]): ConstructionForMaterialSearch | null => {
      let chosen: MaterialConsumption | null = null;
      for (const consumption of consumptions) {
        if (aliveConstructionById.has(consumption.construction_id)) {
          if (chosen === null || consumption.createdAt > chosen.createdAt) {
            chosen = consumption;
          }
        }
      }
      return chosen != null ? aliveConstructionById.get(chosen.construction_id) ?? null : null;
    };

    // 5) 結果を構築(入力ID順を維持)
    const byId = new Map<number, searchedMaterial>();
    for (const m of materials) {
      const aliveConsumptions = (consumptionsMap.get(m.id) ?? []).filter((c) => c.deleted_at == null);
      const result: searchedMaterial = {
        ...m,
        Issuance: m.issuance_id ? issuanceMap.get(m.issuance_id) ?? null : null,
        Lot: m.lot_id ? lotMap.get(m.lot_id) ?? null : null,
        CarryIn: m.carry_in_id ? carryInMap.get(m.carry_in_id) ?? null : null,
        Construction: representativeConstruction(aliveConsumptions),
        Consumptions: aliveConsumptions,
      };
      byId.set(m.id, result);
    }

    // id 昇順で返す(サーバー側の範囲検索と同じ並び)
    return materials
      .map((m) => byId.get(m.id))
      .filter((v): v is searchedMaterial => v != null)
      .sort((a, b) => a.id - b.id);
  }

  // 材料の紐付け(lot_id等)を更新する。ローカルDBに無い材料は新規作成する。
  public async CreateWhenLess(data: materialLink[], ownerId: string) {
    const allMaterials = await this.getAll(ownerId);

    // 既存レコードをid → レコードに引きやすい形で保持
    const allMap = new Map(allMaterials.map((m) => [m.id, m]));

    const lessMaterials = data.filter((d) => !allMap.has(d.id));
    const activeMaterials = data.filter((d) => allMap.has(d.id));

    const currentTime = Time.Now().GetTime();
    const tx = this.db.transaction(this.def.store, "readwrite");

    /** ---- 新規追加分 ---- */
    for (const material of lessMaterials) {
      const putData: AsCache<WithOwner<MaterialBase>> = {
        ...material,
        issuance_id: material.issuance_id ?? null,
        lot_id: material.lot_id ?? null,
        carry_in_id: material.carry_in_id ?? null,
        ownerId: ownerId,
        cachedAt: currentTime,
        createdAt: currentTime,
        updatedAt: currentTime,
      };
      await tx.objectStore(this.def.store).add(putData);
    }

    /** ---- 既存更新分 ---- */
    for (const activeMaterial of activeMaterials) {
      const existing = required(allMap.get(activeMaterial.id), "更新対象の材料QR個体");
      const merged = mergeDefinedColumns(existing, activeMaterial);

      const updateData = {
        ...merged,
        ownerId: ownerId,
        ...this.addPatchBaseColumns(),
      };
      await tx.objectStore(this.def.store).put(updateData);
    }

    await tx.done;
  }

  // CreateWhenLess と同じ組み立て(新規行 / 既存マージ行)を行うが、書き込みはしない。
  // Lot/CarryIn の process(QRスキャン)が、親行の patch と合わせて putRowsAtomic で
  // 1トランザクションに束ねられるようにするための組み立て専用版。
  public async buildLinkRows(data: materialLink[], ownerId: string): Promise<IdbRowPayload[]> {
    const allMaterials = await this.getAll(ownerId);
    const allMap = new Map(allMaterials.map((m) => [m.id, m]));
    const currentTime = Time.Now().GetTime();

    const newRows = data.filter((d) => !allMap.has(d.id)).map((material): AsCache<WithOwner<MaterialBase>> => ({
      ...material,
      issuance_id: material.issuance_id ?? null,
      lot_id: material.lot_id ?? null,
      carry_in_id: material.carry_in_id ?? null,
      ownerId: ownerId,
      cachedAt: currentTime,
      createdAt: currentTime,
      updatedAt: currentTime,
    }));

    const updatedRows = data.filter((d) => allMap.has(d.id)).map((activeMaterial) => {
      const existing = required(allMap.get(activeMaterial.id), "更新対象の材料QR個体");
      const merged = mergeDefinedColumns(existing, activeMaterial);
      return { ...merged, ownerId: ownerId, ...this.addPatchBaseColumns() };
    });

    return [...newRows, ...updatedRows];
  }
}
